import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, attachComponentData } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { catalogGraphRouteRef } from '@backstage/plugin-catalog-graph';
import { Box, Typography, IconButton, Tooltip, Button, CircularProgress, Slider, InputBase, Drawer, Chip, Switch, FormControlLabel, Select } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import TuneIcon from '@material-ui/icons/Tune';
import type { Simulation, SimulationNodeDatum } from 'd3-force';
import { forceSimulation, forceLink, forceManyBody, forceCollide, forceX, forceY } from 'd3-force';
import { zoom as d3zoom, zoomIdentity } from 'd3-zoom';
import { select } from 'd3-selection';
import { drag } from 'd3-drag';
import {
  readDependencyGrades,
  dependencyTier,
  getDependencyColors,
  type DependencyGrade,
} from './dependencyCriticality';
import {
  NODE_BASE_RADIUS,
  nodeRadius as scaledNodeRadius,
  collisionRadius,
  chargeDistanceMax,
  EDGE_WIDTH_RATIOS,
  HAS_PART_WIDTH_RATIO,
  CUSTOMER_LINK_WIDTH_RATIO,
  EDGE_DASH_RATIOS,
  edgeWidth,
  linkDistance,
  clampToScreen,
  LABEL_FONT_RATIO,
  LABEL_GAP_RATIO,
  LABEL_HALO_RATIO,
  ARROW_RATIO,
  ARROW_CLEARANCE_RATIO,
  EDGE_SCREEN_MIN_PX,
  EDGE_SCREEN_MAX_PX,
  LABEL_SCREEN_MIN_PX,
  LABEL_SCREEN_MAX_PX,
  LABEL_HIDE_BELOW_K,
} from './graphScale';
import { computeBlastRadius } from './blastRadius';
import {
  groupByDomain,
  measureDomainCircles,
  separateDomainCircles,
} from './domainClusters';
import {
  getEntityOwner,
  getEntityRegion,
  getEntityEnv,
  getEntityTeam,
  getEntityDomain,
  getEntityOwningVp,
  getEntityCsp,
  getEntityCustomers,
  getEntityPending,
} from './entityFacets';

// ── Types ─────────────────────────────────────────────────────────────────────

interface GraphNode extends SimulationNodeDatum {
  id: string;
  label: string;
  kind: string;
  namespace: string;
  domain: string;
  region: string;
  env: string;
  csp: string;
  team: string;
  owningVp: string;
  /** Multi-valued, unlike the other facets — a system can serve more than one customer. */
  customers: string[];
  parentSystem?: string;
  owner?: string;
  description?: string;
  lifecycle?: string;
  tags?: string[];
}

/** Facets the graph can filter and cluster on. Keys match GraphNode fields. */
const FACETS = [
  { key: 'domain', label: 'Domain', multi: false },
  { key: 'region', label: 'Region', multi: false },
  { key: 'env', label: 'Environment', multi: false },
  { key: 'csp', label: 'CSP', multi: false },
  { key: 'team', label: 'Team', multi: false },
  { key: 'owner', label: 'Owner', multi: false },
  { key: 'owningVp', label: 'Owning VP', multi: false },
  { key: 'customers', label: 'Customer', multi: true },
] as const;

type FacetKey = (typeof FACETS)[number]['key'];

type FacetFilters = Record<FacetKey, string>;

const NO_FACET_FILTERS: FacetFilters = {
  domain: 'all', region: 'all', env: 'all', csp: 'all',
  team: 'all', owner: 'all', owningVp: 'all', customers: 'all',
};

function isMultiFacet(key: FacetKey): boolean {
  return FACETS.find(f => f.key === key)?.multi === true;
}

interface GraphLink {
  id: string;
  source: string | GraphNode;
  target: string | GraphNode;
  relation: string;
  grade?: DependencyGrade;
}

interface SelectedEntity {
  node: GraphNode;
}

// ── Color palette ─────────────────────────────────────────────────────────────

const KIND_COLORS: Record<string, string> = {
  system: '#00897B',
  component: '#1E88E5',
  resource: '#FB8C00',
  api: '#43A047',
  customer: '#AB47BC',
};

/** Overlay glyph drawn on top of a node's circle — the "different icon" for kinds that need one. */
const NODE_ICONS: Record<string, string> = {
  customer: '👥',
};

const CUSTOMER_LINK_COLOR = '#CE93D8';

const DOMAIN_COLORS = [
  '#5C6BC0', '#E91E63', '#FF9800', '#9C27B0', '#00BCD4',
  '#8BC34A', '#F44336', '#3F51B5', '#CDDC39', '#795548',
  '#607D8B', '#FF5722', '#673AB7', '#009688', '#00897B',
];

function domainColor(idx: number): string {
  return DOMAIN_COLORS[idx % DOMAIN_COLORS.length];
}

const FAILURE_RED = '#ff1744';

/** Compact labelled dropdown used by the graph's filter bar. */
function FacetSelect({ label, value, options, onChange, chrome }: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (v: string) => void;
  chrome: Record<string, string>;
}) {
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '0 0 104px', width: 104 }}>
      <Typography style={{ color: chrome.textMuted, fontSize: 9, letterSpacing: 0.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label.toUpperCase()}
      </Typography>
      <Select
        native
        disableUnderline
        value={value}
        onChange={e => onChange(String(e.target.value))}
        inputProps={{ 'aria-label': label }}
        style={{
          color: value === 'all' ? chrome.textMuted : '#58a6ff',
          fontSize: 11,
          width: '100%',
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Select>
    </Box>
  );
}

/** Excludes hand-authored catalog-info.yaml entities (e.g. docs) that never came from the System Registry sync. */
function isFromSystemRegistry(entity: { metadata: { annotations?: Record<string, string> } }): boolean {
  return entity.metadata.annotations?.['verint.com/source'] === 'system-registry';
}

/** Flashing styles for simulated outages, injected alongside the graph. */
const IMPACT_STYLES = `
@keyframes fg-impact-pulse {
  0%, 100% { stroke-opacity: 1; stroke-width: 3px; }
  50% { stroke-opacity: 0.2; stroke-width: 1.5px; }
}
@keyframes fg-node-pulse {
  0%, 100% { filter: drop-shadow(0 0 2px ${FAILURE_RED}); }
  50% { filter: drop-shadow(0 0 10px ${FAILURE_RED}); }
}
.fg-impact-link {
  stroke: ${FAILURE_RED};
  animation: fg-impact-pulse 1.1s ease-in-out infinite;
}
.fg-failed-node {
  stroke: ${FAILURE_RED};
  stroke-width: 3px;
  animation: fg-node-pulse 1.1s ease-in-out infinite;
}
.fg-impacted-node {
  stroke: ${FAILURE_RED};
  stroke-width: 2px;
  animation: fg-node-pulse 1.6s ease-in-out infinite;
}
/* Declared last so an open panel stays identifiable on a failing node. */
.fg-selected-node {
  stroke: #ffeb3b;
  stroke-width: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .fg-impact-link, .fg-failed-node, .fg-impacted-node { animation: none; }
}
`;

// ── Component ─────────────────────────────────────────────────────────────────

export function ForceGraph() {
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const canvasBg = isDark ? '#0d1117' : '#f4f6f8';
  const depColors = useMemo(() => getDependencyColors(isDark), [isDark]);
  // UI chrome (panels, drawer, tooltip) is dark by default; these mirror it in light mode.
  const chrome = {
    panelBg: isDark ? 'rgba(13,17,23,0.92)' : 'rgba(255,255,255,0.92)',
    panelBorder: isDark ? '#30363d' : '#d0d7de',
    textMuted: isDark ? '#8b949e' : '#57606a',
    textHeading: isDark ? '#c9d1d9' : '#24292f',
    textPrimary: isDark ? '#e6edf3' : '#24292f',
    drawerBg: isDark ? '#161b22' : '#ffffff',
    chipBg: isDark ? '#21262d' : '#eaeef2',
    tooltipBg: isDark ? 'rgba(22,27,34,0.95)' : 'rgba(255,255,255,0.97)',
    outageBg: isDark ? 'rgba(13,17,23,0.95)' : 'rgba(255,255,255,0.95)',
    hasPartSwatch: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(66,66,66,0.18)',
  };
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<Simulation<GraphNode, GraphLink> | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<GraphNode[]>([]);
  const [links, setLinks] = useState<GraphLink[]>([]);
  const [depth, setDepth] = useState(1); // 1=systems, 2=+components, 3=+resources/APIs
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
  const [focusNode, setFocusNode] = useState<string | null>(null);
  const [focusHops, setFocusHops] = useState(2);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; node: GraphNode } | null>(null);
  const [failedNodes, setFailedNodes] = useState<Set<string>>(new Set());
  // 0 means unlimited, matching the "All" option.
  const [impactDepth, setImpactDepth] = useState(0);
  const [facetFilters, setFacetFilters] = useState<FacetFilters>(NO_FACET_FILTERS);
  // 'none' drops the rings and the clustering force, leaving a plain force layout.
  const [groupBy, setGroupBy] = useState<FacetKey | 'none'>('domain');
  const [showRings, setShowRings] = useState(true);
  // Read from inside the D3 tick, which is not rebuilt when the toggle flips.
  const showRingsRef = useRef(showRings);
  // Off by default: synthesizing a node per customer can fan out heavily when
  // many systems share the same customer, so it's an opt-in overlay.
  const [showCustomers, setShowCustomers] = useState(false);
  const [showDisplayPanel, setShowDisplayPanel] = useState(false);
  // Node size and gap change the layout, so they are committed on slider
  // release; the draft value keeps the thumb moving while dragging. Edge width
  // is purely visual and applies live.
  // Defaults tuned from user feedback: a smaller node with a thin edge reads
  // better on dense graphs than the original 1×/1×/1× baseline.
  const DEFAULT_NODE_SCALE = 0.7;
  const DEFAULT_GAP_SCALE = 1;
  const DEFAULT_EDGE_SCALE = 0.1;
  const [nodeScale, setNodeScale] = useState(DEFAULT_NODE_SCALE);
  const [nodeScaleDraft, setNodeScaleDraft] = useState(DEFAULT_NODE_SCALE);
  const [gapScale, setGapScale] = useState(DEFAULT_GAP_SCALE);
  const [gapScaleDraft, setGapScaleDraft] = useState(DEFAULT_GAP_SCALE);
  const [edgeScale, setEdgeScale] = useState(DEFAULT_EDGE_SCALE);
  // Edge width is restyled in place, so the D3 closure reads it through a ref.
  const edgeScaleRef = useRef(edgeScale);

  const setFacet = useCallback((key: FacetKey, value: string) => {
    setFacetFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const activeFacetCount = useMemo(
    () => FACETS.filter(f => facetFilters[f.key] !== 'all').length,
    [facetFilters],
  );

  const toggleFailure = useCallback((id: string) => {
    setFailedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const blastRadius = useMemo(
    () => computeBlastRadius(failedNodes, links, impactDepth || Infinity),
    [failedNodes, links, impactDepth],
  );

  const domainIndexMap = useRef<Map<string, number>>(new Map());

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const systemsRes = await catalogApi.getEntities({ filter: { kind: 'System' } });
      // Pending systems aren't usable as dependency targets yet and shouldn't clutter the graph.
      const registrySystems = systemsRes.items.filter(isFromSystemRegistry).filter(e => !getEntityPending(e));

      const graphNodes: GraphNode[] = [];
      const graphLinks: GraphLink[] = [];
      const dMap = new Map<string, number>();
      let domainIdx = 0;

      // Systems (always shown)
      for (const entity of registrySystems) {
        const name = entity.metadata.name;
        const ns = entity.metadata.namespace ?? 'default';
        const domain = getEntityDomain(entity) || 'unknown';
        if (!dMap.has(domain)) dMap.set(domain, domainIdx++);

        graphNodes.push({
          id: `system:${ns}/${name}`,
          label: entity.metadata.title || name,
          kind: 'system',
          namespace: ns,
          domain,
          region: getEntityRegion(entity),
          env: getEntityEnv(entity),
          csp: getEntityCsp(entity),
          team: getEntityTeam(entity),
          owningVp: getEntityOwningVp(entity),
          customers: getEntityCustomers(entity),
          owner: getEntityOwner(entity),
          description: entity.metadata.description ?? '',
          lifecycle: (entity.spec as any)?.lifecycle ?? '',
          tags: entity.metadata.tags ?? [],
        });
      }

      // System dependency links
      for (const entity of registrySystems) {
        const sourceId = `system:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
        const grades = readDependencyGrades(entity);
        for (const rel of entity.relations ?? []) {
          if (rel.type === 'dependsOn' && rel.targetRef.startsWith('system:')) {
            if (graphNodes.find(n => n.id === rel.targetRef)) {
              graphLinks.push({ id: `dep-${sourceId}->${rel.targetRef}`, source: sourceId, target: rel.targetRef, relation: 'dependsOn', grade: grades[rel.targetRef] });
            }
          }
        }
      }

      // Depth 2+: children
      if (depth >= 2) {
        const fetches: Promise<any>[] = [catalogApi.getEntities({ filter: { kind: 'Component' } })];
        if (depth >= 3) {
          fetches.push(catalogApi.getEntities({ filter: { kind: 'Resource' } }));
          fetches.push(catalogApi.getEntities({ filter: { kind: 'API' } }));
        }
        const results = await Promise.all(fetches);
        const children = results.flatMap(r => r.items).filter(isFromSystemRegistry).filter(e => !getEntityPending(e));

        for (const child of children) {
          const childId = `${child.kind.toLowerCase()}:${child.metadata.namespace ?? 'default'}/${child.metadata.name}`;
          const systemRef = (child.spec as any)?.system as string | undefined;
          const parentSystem = systemRef && graphNodes.find(n => n.id === systemRef) ? systemRef : undefined;
          const parent = parentSystem ? graphNodes.find(n => n.id === parentSystem) : undefined;

          graphNodes.push({
            id: childId,
            label: child.metadata.title || child.metadata.name,
            kind: child.kind.toLowerCase(),
            namespace: child.metadata.namespace ?? 'default',
            domain: parent?.domain ?? 'unknown',
            // Children rarely carry their own facet annotations; inheriting the
            // parent's keeps them with their system under any grouping.
            region: getEntityRegion(child) || parent?.region || '',
            env: getEntityEnv(child) || parent?.env || '',
            csp: getEntityCsp(child) || parent?.csp || '',
            team: getEntityTeam(child) || parent?.team || '',
            owningVp: getEntityOwningVp(child) || parent?.owningVp || '',
            customers: getEntityCustomers(child).length ? getEntityCustomers(child) : (parent?.customers ?? []),
            parentSystem,
            owner: getEntityOwner(child) || parent?.owner || '',
            description: child.metadata.description ?? '',
            lifecycle: (child.spec as any)?.lifecycle ?? '',
            tags: child.metadata.tags ?? [],
          });

          if (parentSystem) {
            graphLinks.push({ id: `${parentSystem}->${childId}`, source: parentSystem, target: childId, relation: 'hasPart' });
          }
        }
      }

      domainIndexMap.current = dMap;
      setNodes(graphNodes);
      setLinks(graphLinks);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  }, [catalogApi, depth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Customer overlay ───────────────────────────────────────────────────────
  // Synthesized rather than fetched: a customer isn't a catalog entity, just an
  // attribute shared by the systems that serve it, so one node is created per
  // distinct customer code and wired to every system that lists it.
  const { customerNodes, customerLinks } = useMemo(() => {
    if (!showCustomers) return { customerNodes: [] as GraphNode[], customerLinks: [] as GraphLink[] };
    const byCustomer = new Map<string, GraphNode>();
    const cLinks: GraphLink[] = [];
    for (const n of nodes) {
      if (n.kind !== 'system') continue;
      for (const customer of n.customers ?? []) {
        const custId = `customer:${customer}`;
        if (!byCustomer.has(custId)) {
          byCustomer.set(custId, {
            id: custId,
            label: customer,
            kind: 'customer',
            namespace: 'default',
            domain: '',
            region: '',
            env: '',
            csp: '',
            team: '',
            owningVp: '',
            customers: [],
          });
        }
        cLinks.push({ id: `serves-${n.id}->${custId}`, source: n.id, target: custId, relation: 'servesCustomer' });
      }
    }
    return { customerNodes: [...byCustomer.values()], customerLinks: cLinks };
  }, [nodes, showCustomers]);

  const displayNodes = useMemo(
    () => (showCustomers ? [...nodes, ...customerNodes] : nodes),
    [nodes, customerNodes, showCustomers],
  );
  const displayLinks = useMemo(
    () => (showCustomers ? [...links, ...customerLinks] : links),
    [links, customerLinks, showCustomers],
  );

  // ── Facet filtering + focus mode ──────────────────────────────────────────

  /**
   * Passes every active facet filter except `exclude`. "unknown" matches a
   * missing/empty value. Multi-valued facets (customers) test membership
   * rather than equality, so picking one customer still shows systems that
   * also serve others.
   */
  const passesFacets = useCallback((n: GraphNode, exclude?: FacetKey): boolean => {
    // Synthetic customer nodes carry no facet values of their own; whether they
    // stay visible is decided by whether any of their links survive filtering.
    if (n.kind === 'customer') return true;
    for (const f of FACETS) {
      if (f.key === exclude) continue;
      const want = facetFilters[f.key];
      if (want === 'all') continue;
      if (f.multi) {
        const values = ((n as any)[f.key] as string[] | undefined) ?? [];
        if (want === 'unknown' ? values.length !== 0 : !values.includes(want)) return false;
      } else {
        const value = ((n as any)[f.key] as string | undefined) ?? '';
        if (want === 'unknown' ? value !== '' : value !== want) return false;
      }
    }
    return true;
  }, [facetFilters]);

  // Cross-filtered so a facet only offers values still reachable under the
  // other filters, rather than dead options that would empty the graph.
  // "Unknown" surfaces nodes missing the facet entirely, so gaps are easy to find.
  const facetOptions = useMemo(() => {
    const out = {} as Record<FacetKey, { values: string[]; hasUnknown: boolean }>;
    for (const f of FACETS) {
      const values = new Set<string>();
      let hasUnknown = false;
      for (const n of nodes) {
        if (!passesFacets(n, f.key)) continue;
        if (f.multi) {
          const list = ((n as any)[f.key] as string[] | undefined) ?? [];
          if (list.length) list.forEach(v => values.add(v));
          else hasUnknown = true;
        } else {
          const v = (n as any)[f.key] as string | undefined;
          if (v) values.add(v);
          else hasUnknown = true;
        }
      }
      out[f.key] = { values: [...values].sort(), hasUnknown };
    }
    return out;
  }, [nodes, passesFacets]);

  const getVisibleData = useCallback((): { visibleNodes: GraphNode[]; visibleLinks: GraphLink[] } => {
    let baseNodes = displayNodes;
    let baseLinks = displayLinks;

    if (activeFacetCount > 0) {
      baseNodes = displayNodes.filter(n => passesFacets(n));
      const kept = new Set(baseNodes.map(n => n.id));
      baseLinks = displayLinks.filter(l => {
        const srcId = typeof l.source === 'string' ? l.source : l.source.id;
        const tgtId = typeof l.target === 'string' ? l.target : l.target.id;
        return kept.has(srcId) && kept.has(tgtId);
      });
    }

    // A customer node with no surviving link (its systems all got filtered
    // out) would otherwise float on its own with nothing connecting to it.
    if (showCustomers) {
      const linkedIds = new Set<string>();
      for (const l of baseLinks) {
        linkedIds.add(typeof l.source === 'string' ? l.source : l.source.id);
        linkedIds.add(typeof l.target === 'string' ? l.target : l.target.id);
      }
      baseNodes = baseNodes.filter(n => n.kind !== 'customer' || linkedIds.has(n.id));
    }

    if (!focusNode) return { visibleNodes: baseNodes, visibleLinks: baseLinks };

    const visited = new Set<string>([focusNode]);
    let frontier = [focusNode];
    for (let hop = 0; hop < focusHops; hop++) {
      const next: string[] = [];
      for (const nodeId of frontier) {
        for (const link of baseLinks) {
          const srcId = typeof link.source === 'string' ? link.source : link.source.id;
          const tgtId = typeof link.target === 'string' ? link.target : link.target.id;
          if (srcId === nodeId && !visited.has(tgtId)) { visited.add(tgtId); next.push(tgtId); }
          if (tgtId === nodeId && !visited.has(srcId)) { visited.add(srcId); next.push(srcId); }
        }
      }
      frontier = next;
    }

    const visibleNodes = baseNodes.filter(n => visited.has(n.id));
    const visibleLinks = baseLinks.filter(l => {
      const srcId = typeof l.source === 'string' ? l.source : l.source.id;
      const tgtId = typeof l.target === 'string' ? l.target : l.target.id;
      return visited.has(srcId) && visited.has(tgtId);
    });
    return { visibleNodes, visibleLinks };
  }, [focusNode, focusHops, displayNodes, displayLinks, activeFacetCount, passesFacets, showCustomers]);

  const visibleNodeCount = useMemo(() => getVisibleData().visibleNodes.length, [getVisibleData]);

  // ── Force simulation ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || nodes.length === 0) return;

    const { visibleNodes, visibleLinks } = getVisibleData();
    // Filters can exclude everything; clear rather than leave the last layout up.
    if (visibleNodes.length === 0) {
      select(svgRef.current).selectAll('*').remove();
      return undefined;
    }

    const svg = select(svgRef.current);
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    svg.selectAll('*').remove();
    const g = svg.append('g');

    const zoomBehavior = d3zoom<SVGSVGElement, unknown>().scaleExtent([0.1, 5]);
    svg.call(zoomBehavior);

    (svgRef.current as any).__zoomBehavior = zoomBehavior;
    (svgRef.current as any).__g = g;

    const nodeRadius = (n: GraphNode) => scaledNodeRadius(n.kind, nodeScale);
    const grouped = groupBy !== 'none';
    // Multi-valued facets (customers) can't cluster a node into more than one
    // ring, so a system serving several customers gets its own combined bucket
    // (e.g. "CustomerA + CustomerB") rather than being duplicated or dropped.
    const groupByIsMulti = grouped && isMultiFacet(groupBy as FacetKey);
    const clusterKeyOf = (n: GraphNode): string => {
      if (groupByIsMulti) {
        const list = ((n as any)[groupBy] as string[] | undefined) ?? [];
        return list.length ? [...new Set(list)].sort().join(' + ') : 'unknown';
      }
      return ((n as any)[groupBy] as string | undefined) || 'unknown';
    };
    const nodesByDomain = grouped
      ? groupByDomain(visibleNodes, n => clusterKeyOf(n))
      : new Map<string, GraphNode[]>();
    const domainList = [...nodesByDomain.keys()];
    const groupColorIndex = new Map<string, number>();
    domainList.forEach((key, i) => {
      // Domains keep the colour they were assigned at fetch time so it survives
      // filtering; other facets are coloured by position in the group list.
      groupColorIndex.set(key, groupBy === 'domain' ? domainIndexMap.current.get(key) ?? i : i);
    });

    // Cluster positions by group. The ring grows with the group count so the
    // clusters start far enough apart for the separation force to hold them,
    // and is stretched to the viewport aspect so the layout fills a wide canvas
    // instead of stacking into a column that zoom-to-fit has to shrink.
    const angleStep = (2 * Math.PI) / Math.max(domainList.length, 1);
    const ringRadius = Math.max(
      Math.min(width, height) * 0.22,
      (domainList.length * 150) / (2 * Math.PI),
    );
    const stretch = Math.sqrt(width / Math.max(height, 1));
    const ringX = ringRadius * stretch;
    const ringY = ringRadius / stretch;
    const clusterCenters = new Map<string, { x: number; y: number }>();
    domainList.forEach((d, i) => {
      clusterCenters.set(d, {
        x: width / 2 + Math.cos(i * angleStep) * ringX,
        y: height / 2 + Math.sin(i * angleStep) * ringY,
      });
    });
    const centerOf = (n: GraphNode) => clusterCenters.get(clusterKeyOf(n));
    const groupKeyOf = (n: string | GraphNode) =>
      typeof n === 'string' ? n : clusterKeyOf(n);
    // A link to another group still has to visually connect the two nodes, but
    // at full strength it can out-pull cohesion (0.35) — a node with two or
    // three cross-group dependencies easily gets dragged out of its own ring
    // entirely, landing in the gap between rings instead of inside either one.
    const linkStrength = (d: GraphLink) =>
      grouped && groupKeyOf(d.source) !== groupKeyOf(d.target) ? 0.12 : 0.7;

    // Simulation
    const simulation = forceSimulation(visibleNodes)
      .force('link', forceLink<GraphNode, GraphLink>(visibleLinks).id(d => d.id).distance(d => linkDistance(d.relation, nodeScale, gapScale)).strength(linkStrength))
      // distanceMax caps repulsion at roughly one cluster's width: without it
      // every node pushes every other node, which inflates the whole layout.
      .force('charge', forceManyBody().strength(-60).distanceMax(chargeDistanceMax(gapScale)))
      // Cohesion is deliberately firm: dependency edges to other groups drag
      // stray nodes out, and every stray widens its group's ring.
      .force('x', forceX<GraphNode>(d => centerOf(d)?.x ?? width / 2).strength(grouped ? 0.35 : 0.04))
      .force('y', forceY<GraphNode>(d => centerOf(d)?.y ?? height / 2).strength(grouped ? 0.35 : 0.04))
      .force('collision', forceCollide<GraphNode>().radius(d => collisionRadius(d.kind, nodeScale, gapScale)))
      .alphaDecay(0.035)
      .alphaMin(0.02);

    // Not scaled by alpha: the cluster forces above fade out as the layout
    // settles, so this one gets the last word on ring overlap. Run several
    // passes per tick — the simulation's tick budget (alphaDecay/alphaMin) is
    // fixed regardless of how many groups there are, and with a dozen-plus
    // regions a single 20%-per-tick correction doesn't converge in time.
    if (grouped) {
      simulation.force('domainSeparation', () => {
        for (let pass = 0; pass < 4; pass++) {
          separateDomainCircles(measureDomainCircles(nodesByDomain, nodeRadius));
        }
      });
    }

    simulationRef.current = simulation;

    // Domain cluster backgrounds
    const domainHulls = g.append('g').attr('class', 'domain-hulls');

    // Dependency edge colours, keyed by criticality tier. Markers can't inherit
    // the line stroke reliably across browsers, so define one arrowhead per colour.
    const depTier = (d: GraphLink) =>
      dependencyTier(d.grade ?? { criticality: 'unknown', severity: '' });

    const defs = svg.append('defs');
    Object.entries(depColors).forEach(([key, color]) => {
      defs
        .append('marker')
        .attr('id', `fg-arrow-${key}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 10)
        .attr('refY', 0)
        .attr('markerWidth', NODE_BASE_RADIUS * ARROW_RATIO)
        .attr('markerHeight', NODE_BASE_RADIUS * ARROW_RATIO)
        .attr('markerUnits', 'userSpaceOnUse')
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-3.5L10,0L0,3.5')
        .attr('fill', color);
    });

    // Links
    const linkGroup = g.append('g').attr('class', 'graph-links');
    const hasPartColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(66,66,66,0.18)';
    const baseLinkWidth = (d: GraphLink) =>
      edgeWidth(
        d.relation === 'dependsOn' ? EDGE_WIDTH_RATIOS[depTier(d)]
          : d.relation === 'servesCustomer' ? CUSTOMER_LINK_WIDTH_RATIO
          : HAS_PART_WIDTH_RATIO,
        edgeScaleRef.current,
      );
    const link = linkGroup
      .selectAll('line')
      .data(visibleLinks)
      .join('line')
      .attr('stroke', d => {
        if (d.relation === 'dependsOn') return depColors[depTier(d)];
        if (d.relation === 'servesCustomer') return CUSTOMER_LINK_COLOR;
        return hasPartColor;
      })
      .attr('marker-end', d =>
        d.relation === 'dependsOn' ? `url(#fg-arrow-${depTier(d)})` : null,
      );

    // Nodes
    const nodeGroup = g.append('g').attr('class', 'graph-nodes');
    const node = nodeGroup
      .selectAll<SVGCircleElement, GraphNode>('circle')
      .data(visibleNodes)
      .join('circle')
      .attr('r', d => nodeRadius(d))
      .attr('fill', d => KIND_COLORS[d.kind] ?? '#8b949e')
      .attr('stroke', d => d.id === focusNode ? '#f0883e' : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(66,66,66,0.3)'))
      .attr('stroke-width', d => d.id === focusNode ? 3 : 1)
      .style('cursor', 'pointer');

    // Icon overlay — kinds in NODE_ICONS get a glyph on top of their circle so
    // they read as a distinct "type" at a glance, not just a different colour.
    const iconGroup = g.append('g').attr('class', 'graph-icons');
    const icon = iconGroup
      .selectAll<SVGTextElement, GraphNode>('text')
      .data(visibleNodes.filter(n => NODE_ICONS[n.kind]))
      .join('text')
      .text(d => NODE_ICONS[d.kind])
      .attr('font-size', d => nodeRadius(d) * 1.2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .style('pointer-events', 'none');

    // Labels for every node. Ordered by degree so that when labels are
    // decluttered the structurally important names are the ones that survive.
    const degree = new Map<string, number>();
    for (const l of visibleLinks) {
      const s = typeof l.source === 'string' ? l.source : l.source.id;
      const t = typeof l.target === 'string' ? l.target : l.target.id;
      degree.set(s, (degree.get(s) ?? 0) + 1);
      degree.set(t, (degree.get(t) ?? 0) + 1);
    }
    const labelText = (d: GraphNode) =>
      d.label.length > 18 ? `${d.label.slice(0, 16)}…` : d.label;

    const labelGroup = g.append('g').attr('class', 'graph-labels');
    const labelColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(66,66,66,0.85)';
    const label = labelGroup
      .selectAll<SVGTextElement, GraphNode>('text')
      .data(
        [...visibleNodes].sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0)),
      )
      .join('text')
      .text(labelText)
      .attr('fill', labelColor)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-weight', 500)
      // Halo keeps labels readable where the graph is densest (many link crossings).
      .attr('paint-order', 'stroke')
      .attr('stroke', canvasBg)
      .attr('stroke-linejoin', 'round')
      .style('pointer-events', 'none');

    // Drag
    const dragBehavior = drag<SVGCircleElement, GraphNode>()
      .on('start', (event, d) => { if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
      .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
      .on('end', (event, d) => { if (!event.active) simulation.alphaTarget(0); d.fx = null; d.fy = null; });
    node.call(dragBehavior as any);

    // Click: show detail panel
    node.on('click', (_event, d) => {
      setSelectedEntity({ node: d });
      setTooltip(null);
    });

    // Hover tooltip (namespaced to avoid D3 drag conflicts)
    node.on('pointerover.tooltip', (event, d) => {
      const rect = container.getBoundingClientRect();
      setTooltip({ x: event.clientX - rect.left, y: event.clientY - rect.top - 10, node: d });
    });
    node.on('pointerout.tooltip', () => setTooltip(null));

    // ── Zoom-compensated sizing ─────────────────────────────────────────────
    // Sizes are authored as ratios of the node radius, but a fixed ratio only
    // reads correctly over a narrow zoom band: zoomed out, edges thin to
    // nothing and labels collapse into a wall of text; zoomed in, labels swamp
    // the nodes. Widths and font size are therefore held inside a screen-pixel
    // range, and colliding labels are dropped.

    let currentK = 1;
    let currentFont = NODE_BASE_RADIUS * LABEL_FONT_RATIO;

    const labelAnchorY = (d: GraphNode, font: number) =>
      (d.y ?? 0) - nodeRadius(d) - font * LABEL_GAP_RATIO;

    /** Hides labels whose on-screen boxes collide, keeping the highest-degree ones. */
    const declutterLabels = (k: number, font: number) => {
      const placed: number[][] = [];
      const fontPx = font * k;
      const halfH = fontPx * 0.6;
      label.attr('display', function (d) {
        // 0.29 approximates the average glyph advance of Inter at this weight.
        const halfW = labelText(d).length * fontPx * 0.29;
        const cx = (d.x ?? 0) * k;
        const cy = labelAnchorY(d, font) * k;
        const x0 = cx - halfW;
        const x1 = cx + halfW;
        const y0 = cy - halfH;
        const y1 = cy + halfH;
        for (const p of placed) {
          if (x0 < p[2] && x1 > p[0] && y0 < p[3] && y1 > p[1]) return 'none';
        }
        placed.push([x0, y0, x1, y1]);
        return null;
      });
    };

    const applyScale = (k: number) => {
      currentK = k;
      // The legibility floor scales down with the slider, otherwise anything
      // below 1x would clamp back up and the slider would do nothing.
      const minPx = EDGE_SCREEN_MIN_PX * Math.min(edgeScaleRef.current, 1);
      const widthAt = (d: GraphLink) =>
        clampToScreen(baseLinkWidth(d), k, minPx, EDGE_SCREEN_MAX_PX);
      const dash = `${clampToScreen(NODE_BASE_RADIUS * EDGE_DASH_RATIOS[0] * edgeScaleRef.current, k, 3)} ${clampToScreen(NODE_BASE_RADIUS * EDGE_DASH_RATIOS[1] * edgeScaleRef.current, k, 2)}`;

      link
        .attr('stroke-width', widthAt)
        // Solid = critical, dashed = non-critical (degradation only).
        .attr('stroke-dasharray', d =>
          d.relation === 'dependsOn' && depTier(d) !== 'critical' ? dash : null,
        );

      currentFont = clampToScreen(
        NODE_BASE_RADIUS * LABEL_FONT_RATIO, k, LABEL_SCREEN_MIN_PX, LABEL_SCREEN_MAX_PX,
      );
      label
        .attr('font-size', currentFont)
        .attr('stroke-width', currentFont * LABEL_HALO_RATIO)
        .attr('dy', d => -(nodeRadius(d) + currentFont * LABEL_GAP_RATIO));

      if (k < LABEL_HIDE_BELOW_K) label.attr('display', 'none');
      else declutterLabels(k, currentFont);
    };

    (svgRef.current as any).__applyScale = () => applyScale(currentK);

    zoomBehavior.on('zoom', (event) => {
      g.attr('transform', event.transform);
      applyScale(event.transform.k);
    });

    // Stop dependency edges short of the target node so the arrowhead stays visible.
    const linkEnd = (d: GraphLink) => {
      const s = d.source as GraphNode;
      const t = d.target as GraphNode;
      const tx = t.x ?? 0;
      const ty = t.y ?? 0;
      if (d.relation !== 'dependsOn') return { x: tx, y: ty };
      const dx = tx - (s.x ?? 0);
      const dy = ty - (s.y ?? 0);
      const dist = Math.hypot(dx, dy) || 1;
      const gap = nodeRadius(t) + NODE_BASE_RADIUS * ARROW_CLEARANCE_RATIO;
      return { x: tx - (dx / dist) * gap, y: ty - (dy / dist) * gap };
    };

    // Tick
    const renderPositions = () => {
      link
        .attr('x1', d => (d.source as GraphNode).x ?? 0)
        .attr('y1', d => (d.source as GraphNode).y ?? 0)
        .attr('x2', d => linkEnd(d).x)
        .attr('y2', d => linkEnd(d).y);
      node.attr('cx', d => d.x ?? 0).attr('cy', d => d.y ?? 0);
      icon.attr('x', d => d.x ?? 0).attr('y', d => d.y ?? 0);
      label.attr('x', d => d.x ?? 0).attr('y', d => d.y ?? 0);
      if (currentK >= LABEL_HIDE_BELOW_K) declutterLabels(currentK, currentFont);
      drawRings();
    };

    // Group rings + labels, drawn from the same geometry the separation
    // force uses so what is rendered is what was kept apart.
    function drawRings() {
      domainHulls.selectAll('*').remove();
      if (!grouped || !showRingsRef.current) return;

      for (const circle of measureDomainCircles(nodesByDomain, nodeRadius)) {
        const color = domainColor(groupColorIndex.get(circle.domain) ?? 0);

        domainHulls.append('circle')
          .attr('cx', circle.cx).attr('cy', circle.cy)
          .attr('r', circle.r)
          .attr('fill', color)
          .attr('fill-opacity', 0.06)
          .attr('stroke', color)
          .attr('stroke-width', 2)
          .attr('stroke-opacity', 0.75)
          .attr('stroke-dasharray', '10 7')
          .style('pointer-events', 'none');

        // Scaled off the ring so a large group stays legible zoomed out.
        const fontSize = Math.max(13, Math.min(circle.r * 0.11, 44));
        domainHulls.append('text')
          .attr('x', circle.cx).attr('y', circle.cy - circle.r - fontSize * 0.5)
          .attr('text-anchor', 'middle')
          .attr('font-size', fontSize)
          .attr('font-weight', 800)
          .attr('font-family', 'Inter, sans-serif')
          .attr('letter-spacing', fontSize * 0.06)
          .attr('fill', color)
          // Halo keeps the label readable where it crosses nodes or edges.
          .attr('paint-order', 'stroke')
          .attr('stroke', canvasBg)
          .attr('stroke-width', fontSize * 0.25)
          .attr('stroke-linejoin', 'round')
          .style('pointer-events', 'none')
          .text(circle.domain.toUpperCase());
      }
    }

    (svgRef.current as any).__redrawRings = () => { drawRings(); };

    simulation.on('tick', renderPositions);

    // Settle off-screen rather than animating into place: the layout used to
    // visibly stretch and drift for several seconds on every visit.
    simulation.stop();
    for (let i = 0; i < 300 && simulation.alpha() > simulation.alphaMin(); i++) {
      simulation.tick();
    }
    renderPositions();
    applyScale(1);

    // Fit to the settled layout. rAF so the SVG has been laid out and getBBox
    // reports real geometry.
    const fitHandle = requestAnimationFrame(() => {
      const bbox = (g.node() as SVGGElement)?.getBBox();
      if (bbox && bbox.width > 0) {
        const pad = 80;
        const scale = Math.min(width / (bbox.width + pad * 2), height / (bbox.height + pad * 2), 1.5);
        const tx = width / 2 - (bbox.x + bbox.width / 2) * scale;
        const ty = height / 2 - (bbox.y + bbox.height / 2) * scale;
        (svg as any).call(zoomBehavior.transform, zoomIdentity.translate(tx, ty).scale(scale));
      }
    });

    return () => {
      cancelAnimationFrame(fitHandle);
      simulation.stop();
    };
  }, [nodes, links, getVisibleData, focusNode, depColors, isDark, canvasBg, groupBy, nodeScale, gapScale, showCustomers]);

  useEffect(() => {
    edgeScaleRef.current = edgeScale;
    (svgRef.current as any)?.__applyScale?.();
  }, [edgeScale, nodes]);

  // ── Group ring visibility ─────────────────────────────────────────────────

  // Redraws in place so hiding the rings never restarts the simulation and
  // reshuffles the layout.
  useEffect(() => {
    showRingsRef.current = showRings;
    (svgRef.current as any)?.__redrawRings?.();
  }, [showRings, nodes, groupBy]);

  // ── Failure simulation highlight ──────────────────────────────────────────

  // Applied to the existing DOM rather than during graph construction, so
  // toggling a failure never restarts the simulation and shifts the layout.
  useEffect(() => {
    if (!svgRef.current) return;
    const g = (svgRef.current as any).__g;
    if (!g) return;

    g.select('.graph-links')
      .selectAll('line')
      .classed('fg-impact-link', (d: GraphLink) => blastRadius.impactedLinks.has(d.id));

    g.select('.graph-nodes')
      .selectAll('circle')
      .classed('fg-failed-node', (d: GraphNode) => failedNodes.has(d.id))
      .classed('fg-impacted-node', (d: GraphNode) => blastRadius.impactedNodes.has(d.id));
  }, [blastRadius, failedNodes, nodes, focusNode]);

  // ── Search highlight ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!svgRef.current) return;
    const g = (svgRef.current as any).__g;
    if (!g) return;

    const term = searchTerm.toLowerCase().trim();
    const selectedId = selectedEntity?.node.id;

    g.select('.graph-nodes').selectAll('circle').each(function (this: SVGCircleElement, d: GraphNode) {
      const sel = select(this);
      const match = term && d.label.toLowerCase().includes(term);
      const selected = d.id === selectedId;
      // The selected node stays lit even when a search would dim it.
      const dimmed = term && !match && !selected;
      sel
        .classed('fg-selected-node', selected)
        .attr('opacity', dimmed ? 0.15 : 1)
        .attr('stroke', match ? '#ffeb3b' : d.id === focusNode ? '#f0883e' : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(66,66,66,0.3)'))
        .attr('stroke-width', match ? 3 : d.id === focusNode ? 3 : 1);
    });

    g.select('.graph-labels').selectAll('text').each(function (this: SVGTextElement, d: GraphNode) {
      const match = term && d.label.toLowerCase().includes(term);
      const dimmed = term && !match && d.id !== selectedId;
      const sel = select(this).attr('opacity', dimmed ? 0.15 : 1);
      // Force a match back into view even if decluttering had dropped it.
      if (match) sel.attr('display', null);
    });

    g.select('.graph-links').selectAll('line').attr('opacity', term ? 0.08 : 1);
  }, [searchTerm, nodes, focusNode, selectedEntity, isDark]);

  const zoomToSearch = useCallback(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return;
    const match = nodes.find(n => n.label.toLowerCase().includes(term));
    if (!match) return;
    // Enter focus mode on the matched node so depth changes show only its neighbors
    setFocusNode(match.id);
  }, [searchTerm, nodes]);

  // ── Zoom controls ─────────────────────────────────────────────────────────

  const handleZoomIn = () => {
    const svg = select(svgRef.current!) as any;
    const zoomBehavior = (svgRef.current as any)?.__zoomBehavior;
    if (zoomBehavior) svg.transition().duration(300).call(zoomBehavior.scaleBy, 1.5);
  };
  const handleZoomOut = () => {
    const svg = select(svgRef.current!) as any;
    const zoomBehavior = (svgRef.current as any)?.__zoomBehavior;
    if (zoomBehavior) svg.transition().duration(300).call(zoomBehavior.scaleBy, 0.67);
  };
  const handleFit = () => {
    const svg = select(svgRef.current!) as any;
    const zoomBehavior = (svgRef.current as any)?.__zoomBehavior;
    const g = (svgRef.current as any)?.__g;
    if (!zoomBehavior || !g || !containerRef.current) return;
    const bbox = (g.node() as SVGGElement)?.getBBox();
    if (!bbox || !bbox.width) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    const pad = 60;
    const scale = Math.min(width / (bbox.width + pad * 2), height / (bbox.height + pad * 2), 1.5);
    const tx = width / 2 - (bbox.x + bbox.width / 2) * scale;
    const ty = height / 2 - (bbox.y + bbox.height / 2) * scale;
    svg.transition().duration(500).call(zoomBehavior.transform, zoomIdentity.translate(tx, ty).scale(scale));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" style={{ height: '100%' }}>
        <CircularProgress size={32} style={{ color: '#58a6ff' }} />
        <Typography style={{ color: chrome.textMuted, marginLeft: 12 }}>Loading catalog graph...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ height: '100%' }}>
        <Typography style={{ color: '#f85149' }}>{error}</Typography>
        <Button onClick={fetchData} style={{ color: '#58a6ff', marginTop: 8 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', background: canvasBg, borderRadius: 8, overflow: 'hidden' }}>
      <style>{IMPACT_STYLES}</style>

      {/* Simulated outages — kept visible so flashing is never unexplained */}
      {failedNodes.size > 0 && (
        <Box style={{
          position: 'absolute', bottom: 8, left: 8, zIndex: 12, maxWidth: 380,
          background: chrome.outageBg, border: `1px solid ${FAILURE_RED}`,
          borderRadius: 6, padding: '6px 10px',
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Typography style={{ color: FAILURE_RED, fontSize: 11, fontWeight: 700 }}>
              SIMULATED OUTAGE · {blastRadius.impactedNodes.size} impacted
            </Typography>
            <Box style={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => setFailedNodes(new Set())}
              style={{ color: chrome.textMuted, textTransform: 'none', fontSize: 10, padding: '0 4px', minWidth: 0 }}
            >
              Clear all
            </Button>
          </Box>

          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Typography style={{ color: chrome.textMuted, fontSize: 10 }}>Depth</Typography>
            <Select
              native
              value={impactDepth}
              onChange={e => setImpactDepth(Number(e.target.value))}
              style={{ color: chrome.textPrimary, fontSize: 11 }}
            >
              <option value={1}>1 — directly depending</option>
              <option value={2}>2 — plus their dependents</option>
              <option value={3}>3 hops</option>
              <option value={4}>4 hops</option>
              <option value={5}>5 hops</option>
              <option value={0}>All (full cascade)</option>
            </Select>
          </Box>
          <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[...failedNodes].map(id => (
              <Chip
                key={id}
                size="small"
                label={nodes.find(n => n.id === id)?.label ?? id}
                onDelete={() => toggleFailure(id)}
                style={{ background: 'rgba(255,23,68,0.18)', color: '#ff8a80', fontSize: 10 }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Top bar — one row; scrolls horizontally rather than wrapping on narrow viewports */}
      <Box style={{
        position: 'absolute', top: 8, left: 8, right: 8, zIndex: 10,
        display: 'flex', alignItems: 'stretch', gap: 8, flexWrap: 'nowrap', overflowX: 'auto',
      }}>
        {/* Search — absorbs the row's spare width so the bar reads as one full row */}
        <Box style={{ display: 'flex', alignItems: 'center', background: chrome.panelBg, borderRadius: 6, border: `1px solid ${chrome.panelBorder}`, padding: '2px 8px', flex: '1 1 150px', minWidth: 150 }}>
          <SearchIcon style={{ color: chrome.textMuted, fontSize: 18, marginRight: 4 }} />
          <InputBase
            placeholder="Search nodes…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') zoomToSearch(); }}
            style={{ color: chrome.textPrimary, fontSize: 13, flex: 1 }}
            inputProps={{ style: { padding: '4px 0' } }}
          />
        </Box>

        {/* Depth slider */}
        <Box style={{ display: 'flex', alignItems: 'center', background: chrome.panelBg, borderRadius: 6, border: `1px solid ${chrome.panelBorder}`, padding: '4px 12px', gap: 8, flex: '0 0 240px' }}>
          <Typography style={{ color: chrome.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>Depth</Typography>
          <Slider
            value={depth}
            onChange={(_e, v) => setDepth(v as number)}
            min={1} max={3} step={1}
            marks={[{ value: 1, label: 'Systems' }, { value: 2, label: '+Components' }, { value: 3, label: '+All' }]}
            style={{ color: '#58a6ff', flex: 1 }}
          />
        </Box>

        {/* Filters — same facets as the catalog table, plus how to cluster them */}
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap', flex: '0 0 auto',
          background: chrome.panelBg, borderRadius: 6, border: `1px solid ${chrome.panelBorder}`,
          padding: '2px 10px',
        }}>
          <FacetSelect
            label="Group by"
            value={groupBy}
            options={[...FACETS.map(f => ({ value: f.key, label: f.label })), { value: 'none', label: 'No grouping' }]}
            onChange={v => setGroupBy(v as FacetKey | 'none')}
            chrome={chrome}
          />

          <Box style={{ width: 1, alignSelf: 'stretch', background: chrome.panelBorder }} />

          {FACETS.map(f => (
            <FacetSelect
              key={f.key}
              label={f.label}
              value={facetFilters[f.key]}
              options={[
                { value: 'all', label: 'All' },
                ...(facetOptions[f.key].hasUnknown ? [{ value: 'unknown', label: 'Unknown' }] : []),
                ...facetOptions[f.key].values.map(v => ({ value: v, label: v })),
              ]}
              onChange={v => setFacet(f.key, v)}
              chrome={chrome}
            />
          ))}

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showRings}
                onChange={e => setShowRings(e.target.checked)}
                color="primary"
                disabled={groupBy === 'none'}
              />
            }
            label={<Typography style={{ color: chrome.textMuted, fontSize: 11 }}>Bubbles</Typography>}
            style={{ marginLeft: 0, marginRight: 0 }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showCustomers}
                onChange={e => setShowCustomers(e.target.checked)}
                color="primary"
              />
            }
            label={<Typography style={{ color: chrome.textMuted, fontSize: 11 }}>{NODE_ICONS.customer} Customers</Typography>}
            style={{ marginLeft: 0, marginRight: 0 }}
          />

          {activeFacetCount > 0 && (
            <Button
              size="small"
              onClick={() => setFacetFilters(NO_FACET_FILTERS)}
              style={{ color: chrome.textMuted, textTransform: 'none', fontSize: 11, padding: '0 6px', minWidth: 0, whiteSpace: 'nowrap' }}
            >
              Clear ({activeFacetCount})
            </Button>
          )}
        </Box>

        {/* Focus mode */}
        {focusNode && (
          <Box style={{ display: 'flex', alignItems: 'center', background: chrome.panelBg, borderRadius: 6, border: '1px solid #f0883e', padding: '4px 12px', gap: 8, flex: '0 0 auto' }}>
            <CenterFocusStrongIcon style={{ color: '#f0883e', fontSize: 16 }} />
            <Typography style={{ color: '#f0883e', fontSize: 11, whiteSpace: 'nowrap' }}>
              {nodes.find(n => n.id === focusNode)?.label ?? ''}
            </Typography>
            <Slider
              value={focusHops}
              onChange={(_e, v) => setFocusHops(v as number)}
              min={1} max={5} step={1}
              marks
              style={{ color: '#f0883e', width: 80 }}
            />
            <Typography style={{ color: chrome.textMuted, fontSize: 10, whiteSpace: 'nowrap' }}>{focusHops} hops</Typography>
            <IconButton size="small" onClick={() => setFocusNode(null)} style={{ color: chrome.textMuted, padding: 2 }}>
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        {/* Zoom */}
        <Box style={{ display: 'flex', alignItems: 'center', gap: 2, background: chrome.panelBg, borderRadius: 6, padding: '2px 4px', border: `1px solid ${chrome.panelBorder}`, flex: '0 0 auto' }}>
          <Tooltip title="Zoom in"><IconButton size="small" onClick={handleZoomIn} style={{ color: chrome.textMuted }}><ZoomInIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Zoom out"><IconButton size="small" onClick={handleZoomOut} style={{ color: chrome.textMuted }}><ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Fit to view"><IconButton size="small" onClick={handleFit} style={{ color: chrome.textMuted }}><ZoomOutMapIcon fontSize="small" /></IconButton></Tooltip>
          <Button
            size="small"
            onClick={() => setShowDisplayPanel(v => !v)}
            startIcon={<TuneIcon style={{ fontSize: 16 }} />}
            style={{ color: showDisplayPanel ? '#58a6ff' : chrome.textMuted, textTransform: 'none', fontSize: 11, minWidth: 0, whiteSpace: 'nowrap' }}
          >
            Sizing
          </Button>
        </Box>
      </Box>

      {/* Sizing — proportions depend on how dense the graph is, so they are tunable */}
      {showDisplayPanel && (
        <Box style={{
          position: 'absolute', top: 62, right: 8, zIndex: 11, width: 210,
          background: chrome.panelBg, border: `1px solid ${chrome.panelBorder}`,
          borderRadius: 6, padding: '8px 14px 4px',
        }}>
          {([
            ['Node size', nodeScaleDraft, setNodeScaleDraft, setNodeScale, 0.4, 4],
            ['Gap', gapScaleDraft, setGapScaleDraft, setGapScale, 0.4, 6],
          ] as const).map(([text, draft, setDraft, commit, min, max]) => (
            <Box key={text}>
              <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography style={{ color: chrome.textMuted, fontSize: 10 }}>{text}</Typography>
                <Typography style={{ color: chrome.textMuted, fontSize: 10 }}>{draft.toFixed(1)}×</Typography>
              </Box>
              <Slider
                value={draft}
                min={min} max={max} step={0.1}
                onChange={(_e, v) => setDraft(v as number)}
                onChangeCommitted={(_e, v) => commit(v as number)}
                style={{ color: '#58a6ff', padding: '6px 0' }}
              />
            </Box>
          ))}

          <Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography style={{ color: chrome.textMuted, fontSize: 10 }}>Edge width</Typography>
              <Typography style={{ color: chrome.textMuted, fontSize: 10 }}>{edgeScale.toFixed(1)}×</Typography>
            </Box>
            <Slider
              value={edgeScale}
              min={0.1} max={6} step={0.1}
              onChange={(_e, v) => setEdgeScale(v as number)}
              style={{ color: '#58a6ff', padding: '6px 0' }}
            />
          </Box>

          <Button
            size="small"
            onClick={() => {
              setNodeScale(DEFAULT_NODE_SCALE); setNodeScaleDraft(DEFAULT_NODE_SCALE);
              setGapScale(DEFAULT_GAP_SCALE); setGapScaleDraft(DEFAULT_GAP_SCALE);
              setEdgeScale(DEFAULT_EDGE_SCALE);
            }}
            style={{ color: chrome.textMuted, textTransform: 'none', fontSize: 11, padding: 0, minWidth: 0 }}
          >
            Reset
          </Button>
        </Box>
      )}

      {/* Legend */}
      <Box style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 10, background: chrome.panelBg, borderRadius: 6, padding: '6px 10px', border: `1px solid ${chrome.panelBorder}`, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {Object.entries(KIND_COLORS)
          .filter(([kind]) => kind !== 'customer' || showCustomers)
          .map(([kind, color]) => (
          <Box key={kind} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, lineHeight: 1 }}>
              {NODE_ICONS[kind] ?? ''}
            </Box>
            <Typography style={{ color: chrome.textMuted, fontSize: 10, textTransform: 'capitalize' }}>{kind}</Typography>
          </Box>
        ))}
        <Box style={{ borderTop: `1px solid ${chrome.panelBorder}`, marginTop: 2, paddingTop: 4 }}>
          <Typography style={{ color: chrome.textHeading, fontSize: 9, fontWeight: 600, marginBottom: 3 }}>Lines</Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <Box style={{ width: 20, height: 0, borderTop: `${edgeWidth(HAS_PART_WIDTH_RATIO, edgeScale)}px solid ${chrome.hasPartSwatch}` }} />
            <Typography style={{ color: chrome.textMuted, fontSize: 9 }}>hasPart (parent→child)</Typography>
          </Box>
          {showCustomers && (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <Box style={{ width: 20, height: 0, borderTop: `${edgeWidth(CUSTOMER_LINK_WIDTH_RATIO, edgeScale)}px dashed ${CUSTOMER_LINK_COLOR}` }} />
              <Typography style={{ color: chrome.textMuted, fontSize: 9 }}>serves customer</Typography>
            </Box>
          )}
          {([
            ['critical', 'solid', 'Critical dependency'],
            ['high', 'dashed', 'Non-critical (high)'],
            ['medium', 'dashed', 'Non-critical (medium)'],
            ['low', 'dashed', 'Non-critical (low/unspecified)'],
            ['unknown', 'dashed', 'Unknown (not yet ingested)'],
          ] as const).map(([tier, style, text]) => (
            <Box key={tier} style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <Box style={{ width: 20, height: 0, borderTop: `${edgeWidth(EDGE_WIDTH_RATIOS[tier], edgeScale)}px ${style} ${getDependencyColors(true)[tier]}` }} />
              <Typography style={{ color: chrome.textMuted, fontSize: 9 }}>{text}</Typography>
            </Box>
          ))}
        </Box>
        <Box style={{ borderTop: `1px solid ${chrome.panelBorder}`, marginTop: 2, paddingTop: 2 }}>
          <Typography style={{ color: chrome.textMuted, fontSize: 9 }}>
            {groupBy === 'none' || !showRings
              ? 'Grouping off'
              : `Dashed circles = ${FACETS.find(f => f.key === groupBy)?.label.toLowerCase()} clusters`}
          </Typography>
        </Box>
      </Box>

      <svg ref={svgRef} width="100%" height="100%" />

      {nodes.length > 0 && visibleNodeCount === 0 && (
        <Box style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none',
        }}>
          <Typography style={{ color: chrome.textMuted, fontSize: 13 }}>
            No entities match the current filters.
          </Typography>
          <Button
            size="small"
            onClick={() => setFacetFilters(NO_FACET_FILTERS)}
            style={{ color: '#58a6ff', textTransform: 'none', fontSize: 12, pointerEvents: 'auto' }}
          >
            Clear filters
          </Button>
        </Box>
      )}

      {/* Detail side panel */}
      <Drawer
        anchor="right"
        open={!!selectedEntity}
        onClose={() => setSelectedEntity(null)}
        variant="persistent"
        PaperProps={{ style: { position: 'absolute', width: 320, background: chrome.drawerBg, borderLeft: `1px solid ${chrome.panelBorder}`, color: chrome.textPrimary } }}
        ModalProps={{ container: containerRef.current, style: { position: 'absolute' } }}
      >
        {selectedEntity && (
          <Box style={{ padding: 16, height: '100%', overflow: 'auto' }}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <Typography style={{ fontSize: 16, fontWeight: 700 }}>{selectedEntity.node.label}</Typography>
              <IconButton size="small" onClick={() => setSelectedEntity(null)} style={{ color: chrome.textMuted }}><CloseIcon fontSize="small" /></IconButton>
            </Box>

            <Box style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              <Chip size="small" label={selectedEntity.node.kind} style={{ background: KIND_COLORS[selectedEntity.node.kind] ?? chrome.textMuted, color: '#fff', fontSize: 11 }} />
              {selectedEntity.node.lifecycle && <Chip size="small" label={selectedEntity.node.lifecycle} variant="outlined" style={{ borderColor: chrome.panelBorder, color: chrome.textMuted, fontSize: 11 }} />}
            </Box>

            {selectedEntity.node.description && (
              <Box style={{ marginBottom: 12 }}>
                <Typography style={{ color: chrome.textMuted, fontSize: 10, marginBottom: 2 }}>DESCRIPTION</Typography>
                <Typography style={{ fontSize: 12, lineHeight: 1.4 }}>{selectedEntity.node.description}</Typography>
              </Box>
            )}

            <Box style={{ marginBottom: 12 }}>
              <Typography style={{ color: chrome.textMuted, fontSize: 10, marginBottom: 2 }}>DOMAIN</Typography>
              <Typography style={{ fontSize: 12 }}>{selectedEntity.node.domain}</Typography>
            </Box>

            {selectedEntity.node.owner && (
              <Box style={{ marginBottom: 12 }}>
                <Typography style={{ color: chrome.textMuted, fontSize: 10, marginBottom: 2 }}>OWNER</Typography>
                <Typography style={{ fontSize: 12 }}>{selectedEntity.node.owner}</Typography>
              </Box>
            )}

            {selectedEntity.node.tags && selectedEntity.node.tags.length > 0 && (
              <Box style={{ marginBottom: 12 }}>
                <Typography style={{ color: chrome.textMuted, fontSize: 10, marginBottom: 4 }}>TAGS</Typography>
                <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {selectedEntity.node.tags.map(t => <Chip key={t} size="small" label={t} style={{ background: chrome.chipBg, color: chrome.textMuted, fontSize: 10 }} />)}
                </Box>
              </Box>
            )}

            {selectedEntity.node.kind !== 'customer' && (
              <Box style={{ borderTop: `1px solid ${chrome.panelBorder}`, paddingTop: 12, marginBottom: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={failedNodes.has(selectedEntity.node.id)}
                      onChange={() => toggleFailure(selectedEntity.node.id)}
                    />
                  }
                  label={
                    <Typography style={{ fontSize: 12, color: failedNodes.has(selectedEntity.node.id) ? FAILURE_RED : chrome.textPrimary }}>
                      Simulate failure
                    </Typography>
                  }
                />
                <Typography style={{ color: chrome.textMuted, fontSize: 10, lineHeight: 1.4 }}>
                  Flashes every system that would go down with it, following critical
                  dependencies and anything this system contains. Stays on until switched
                  off, so several systems can be failed at once.
                </Typography>
                {failedNodes.has(selectedEntity.node.id) && (
                  <Typography style={{ color: FAILURE_RED, fontSize: 11, fontWeight: 600, marginTop: 6 }}>
                    {blastRadius.impactedNodes.size === 0
                      ? 'No downstream systems depend on this critically.'
                      : `${blastRadius.impactedNodes.size} system${blastRadius.impactedNodes.size === 1 ? '' : 's'} in the blast radius.`}
                  </Typography>
                )}
              </Box>
            )}

            <Box style={{ borderTop: `1px solid ${chrome.panelBorder}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {selectedEntity.node.kind !== 'customer' && (
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                  onClick={() => {
                    const [kindPart, rest] = selectedEntity.node.id.split(':');
                    const [ns, name] = rest.split('/');
                    navigate(`/catalog/${ns}/${kindPart}/${name}`);
                  }}
                  style={{ color: '#58a6ff', textTransform: 'none', fontSize: 12, justifyContent: 'flex-start' }}
                >
                  Open in Catalog
                </Button>
              )}
              <Button
                size="small"
                startIcon={<CenterFocusStrongIcon style={{ fontSize: 14 }} />}
                onClick={() => { setFocusNode(selectedEntity.node.id); setSelectedEntity(null); }}
                style={{ color: '#f0883e', textTransform: 'none', fontSize: 12, justifyContent: 'flex-start' }}
              >
                Focus on this ({focusHops} hops)
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Tooltip (rendered after SVG to ensure z-stacking) */}
      {tooltip && (!selectedEntity || selectedEntity.node.id !== tooltip.node.id) && (
        <Box style={{
          position: 'absolute', left: tooltip.x, top: tooltip.y,
          background: chrome.tooltipBg, border: `1px solid ${chrome.panelBorder}`,
          borderRadius: 6, padding: '6px 10px', zIndex: 30, pointerEvents: 'none',
          transform: 'translate(-50%, -100%)',
        }}>
          <Typography style={{ color: chrome.textPrimary, fontSize: 12, fontWeight: 600 }}>{tooltip.node.label}</Typography>
          <Typography style={{ color: chrome.textMuted, fontSize: 10 }}>{tooltip.node.kind} · {tooltip.node.domain}</Typography>
          {failedNodes.has(tooltip.node.id) && (
            <Typography style={{ color: FAILURE_RED, fontSize: 10, fontWeight: 700 }}>
              Simulated failure
            </Typography>
          )}
          {blastRadius.impactedNodes.has(tooltip.node.id) && (
            <Typography style={{ color: FAILURE_RED, fontSize: 10, fontWeight: 600 }}>
              Impacted · {blastRadius.depthByNode.get(tooltip.node.id)} hop
              {blastRadius.depthByNode.get(tooltip.node.id) === 1 ? '' : 's'} from failure
            </Typography>
          )}
        </Box>
      )}
    </div>
  );
}

attachComponentData(ForceGraph, 'core.mountPoint', catalogGraphRouteRef);
